import { Injectable, inject } from '@angular/core';

import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, exhaustMap, tap } from 'rxjs';

import { ToastErrorComponent } from '@tsq-web/toast';

import { RegisterService } from '../../shared/user/register.service';
import { RegistrationRequest } from '../../shared/user/registration-request.model';
import { RegistrationSearchRequest } from '../../shared/user/registration-search-request.model';
import { UserRegister } from '../../shared/user/user-register.model';

interface RegistrationState {
  userRegister: UserRegister;
  userFetchStatus: 'pending' | 'loading' | 'success' | 'failure';
  registrationStatus: 'pending' | 'loading' | 'success' | 'failure';
}

const initialState: RegistrationState = {
  userRegister: undefined,
  userFetchStatus: 'pending',
  registrationStatus: 'pending',
};

@Injectable()
export class RegistrationStore extends ComponentStore<RegistrationState> {
  private readonly registerService = inject(RegisterService);
  private readonly toastr = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  readonly userRegister$ = this.select(({ userRegister }) => userRegister);
  readonly registrationStatus$ = this.select(({ registrationStatus }) => registrationStatus);
  readonly userFetchStatus$ = this.select(({ userFetchStatus }) => userFetchStatus);

  readonly fetchUserRegister = this.effect((data$: Observable<RegistrationSearchRequest>) =>
    data$.pipe(
      tap(() => this.patchState({ userFetchStatus: 'loading' })),
      exhaustMap(searchRequest =>
        this.registerService.registrationSearch(searchRequest).pipe(
          tapResponse(
            userRegister => this.patchState({ userFetchStatus: 'success', userRegister }),
            () => this.patchState({ userFetchStatus: 'failure' }),
          ),
        ),
      ),
    ),
  );

  readonly signUp = this.effect((data$: Observable<RegistrationRequest>) =>
    data$.pipe(
      tap(() => this.patchState({ registrationStatus: 'loading' })),
      exhaustMap(registrationRequest =>
        this.registerService.registration(registrationRequest).pipe(
          tapResponse(
            () => this.patchState({ registrationStatus: 'success' }),
            () => {
              this.toastr.show(
                this.translateService.instant('COMMON_ERROR_TEXT'),
                this.translateService.instant('COMMON_ERROR_TITLE'),
                { toastComponent: ToastErrorComponent },
              );
              this.patchState({ registrationStatus: 'failure' });
            },
          ),
        ),
      ),
    ),
  );

  constructor() {
    super(initialState);
  }
}
